/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  Users, ScrollText, Home, Banknote, FileText, Gift, Coins, Calculator,
  CheckCircle2, AlertTriangle, ArrowRight,
} from 'lucide-react';

import SEO from '@/components/SEO';
import LibraryPageLayout from '@/components/library/LibraryPageLayout';
import LibraryStructuredData from '@/components/library/LibraryStructuredData';
import {
  LibraryHero,
  SectionHeader,
  FeatureCheckItem,
  LibraryFAQ,
  LibraryFinalCta,
  GuideMeta,
  OfficialSources,
  relatedGuides,
} from '@/components/library/sections';
import { guideSeoFor } from '@/data/guideSeo';

const { sources: SOURCES_DECL, howTo: HOWTO_DECL } = guideSeoFor('/declaration-succession');

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const CONTENT_CARDS = [
  {
    Icon: Users,
    title: 'Héritiers',
    description: 'La liste précise des personnes qui héritent : enfants, conjoint, parents éloignés. Avec leur quote-part exacte exprimée en fractions du patrimoine.',
    to: '/declaration-succession',
    ctaLabel: 'En savoir plus',
  },
  {
    Icon: ScrollText,
    title: 'Défunt',
    description: 'L\'identité complète du défunt, sa date et son lieu de décès, son régime matrimonial et l\'état civil de ses ayants droit.',
    to: '/declaration-succession',
    ctaLabel: 'En savoir plus',
  },
  {
    Icon: Home,
    title: 'Biens immobiliers',
    description: 'Maison, appartement, terrain : chaque bien est listé avec son adresse, sa quote-part transmise et sa valeur retenue au jour du décès.',
    to: '/declaration-succession',
    ctaLabel: 'En savoir plus',
  },
  {
    Icon: Banknote,
    title: 'Comptes bancaires',
    description: 'Comptes courants, livrets, contrats d\'assurance-vie, portefeuilles titres : tous les avoirs financiers du défunt et leur solde à la date du décès.',
    to: '/declaration-succession',
    ctaLabel: 'En savoir plus',
  },
  {
    Icon: AlertTriangle,
    title: 'Dettes',
    description: 'Tout ce qui réduit l\'actif successoral : crédits en cours, impôts dus, frais funéraires, factures impayées. Le passif change le montant final.',
    to: '/declaration-succession',
    ctaLabel: 'En savoir plus',
  },
  {
    Icon: Gift,
    title: 'Donations antérieures',
    description: 'Les donations consenties par le défunt de son vivant. Elles sont rappelées car elles peuvent modifier les parts ou être rapportées à la succession.',
    to: '/declaration-succession',
    ctaLabel: 'En savoir plus',
  },
  {
    Icon: Coins,
    title: 'Valeur du patrimoine',
    description: 'L\'actif total moins le passif total donne l\'actif net taxable. C\'est ce montant qui sert de base au calcul des droits.',
    to: '/declaration-succession',
    ctaLabel: 'En savoir plus',
  },
  {
    Icon: Calculator,
    title: 'Calcul des droits',
    description: 'Le détail des droits dus à l\'administration fiscale, après application des abattements personnels propres à chaque héritier.',
    to: '/declaration-succession',
    ctaLabel: 'En savoir plus',
  },
];

const COMMON_ERRORS = [
  'Oublier une donation faite par le défunt avant son décès, en particulier les dons manuels non déclarés.',
  'Sous-évaluer un bien immobilier — l\'administration fiscale peut redresser jusqu\'à trois ans après le dépôt.',
  'Ne pas mentionner un compte bancaire détenu seul ou à l\'étranger, même de faible montant.',
  'Ignorer les dettes du défunt, alors qu\'elles diminuent la base taxable et donc les droits à payer.',
  'Confondre la valeur d\'achat d\'un bien avec sa valeur vénale réelle au jour du décès.',
  'Oublier de demander les abattements auxquels un héritier a droit (handicap, transmission entre époux…).',
  'Déposer la déclaration hors délai et payer des pénalités évitables.',
];

const AUTO_CHECKS = [
  'Personnes mentionnées et leur lien avec le défunt',
  'Biens immobiliers et mobiliers identifiés',
  'Comptes bancaires et avoirs financiers',
  'Montants déclarés et soldes au jour du décès',
  'Donations antérieures rapportées à la succession',
  'Clauses fiscales et abattements appliqués',
  'Questions à poser à votre notaire',
  'Démarches restantes à effectuer',
];

const FAQ_ITEMS = [
  {
    q: 'Qu\'est-ce qu\'une déclaration de succession ?',
    a: 'La déclaration de succession est un document fiscal officiel qui dresse l\'inventaire complet de ce qu\'a laissé une personne décédée et qui calcule les droits de succession à payer à l\'État. Elle liste les héritiers, les biens (immobiliers, comptes bancaires, placements), les dettes éventuelles et les donations antérieures. Elle est déposée à l\'administration fiscale par le notaire ou par les héritiers eux-mêmes.',
  },
  {
    q: 'Qui doit la signer ?',
    a: 'La déclaration est en principe signée par l\'ensemble des héritiers ou par un seul d\'entre eux mandaté par les autres. Dans la pratique, lorsqu\'un notaire est saisi du dossier, c\'est lui qui la prépare et la fait signer aux héritiers. Le conjoint survivant exonéré de droits n\'est pas dispensé de figurer sur la déclaration.',
  },
  {
    q: 'Quand est-elle obligatoire ?',
    a: 'Elle est obligatoire dès qu\'une succession est ouverte, sauf dans quelques cas spécifiques : succession dont l\'actif brut est inférieur à 50 000 € transmise en ligne directe ou au conjoint survivant lorsqu\'aucune donation antérieure n\'a été consentie, ou patrimoine inférieur à 3 000 € pour les autres héritiers.',
  },
  {
    q: 'Quel est le délai pour la déposer ?',
    a: 'Le délai légal est de six mois à compter du décès si celui-ci a lieu en France métropolitaine, et de douze mois s\'il a lieu à l\'étranger ou dans certains DOM. Tout retard entraîne des intérêts de retard et, après quelques mois, des pénalités fiscales qui s\'ajoutent aux droits déjà dus.',
  },
  {
    q: 'Quels documents faut-il fournir au notaire ?',
    a: 'Les pièces classiques sont : acte de décès, livret de famille, contrat de mariage, testament éventuel, titres de propriété immobilière, relevés de comptes bancaires à la date du décès, attestations d\'assurance-vie, factures de frais funéraires et toute donation antérieure connue.',
  },
  {
    q: 'Comment HÉRITA l\'analyse-t-il ?',
    a: 'HÉRITA lit la déclaration de succession dans son intégralité, identifie automatiquement les héritiers, leurs parts, les biens immobiliers, les comptes bancaires, les dettes et les donations rapportées. Il met en avant l\'actif net taxable et explique comment les droits ont été calculés. Vous obtenez en quelques minutes une lecture claire, structurée et personnalisée.',
  },
  {
    q: 'Faut-il forcément un notaire ?',
    a: "La présence d'un notaire est obligatoire dès lors qu'il existe un bien immobilier, un testament, un contrat de mariage modifiant la quotité disponible, ou si la succession dépasse 5 000 €. C'est lui qui établit en amont l'acte de notoriété — le document qui identifie officiellement les héritiers et débloque toutes les démarches qui suivent. Dans les autres cas, les héritiers peuvent en théorie remplir eux-mêmes la déclaration — c'est rare en pratique.",
  },
  {
    q: 'Quels sont les droits à payer ?',
    a: 'Les droits dépendent du lien de parenté et du montant reçu, après application d\'un abattement personnel. Pour un enfant, l\'abattement est de 100 000 € puis le barème progressif s\'applique. Pour un conjoint ou partenaire de PACS, l\'exonération est totale. Pour un frère ou une sœur, l\'abattement est de 15 932 € et le taux nettement plus élevé.',
  },
  {
    q: 'Que faire si je n\'ai pas l\'argent pour payer les droits ?',
    a: 'Plusieurs solutions existent : demander un paiement fractionné sur cinq ans (dix ans pour la transmission d\'une entreprise), demander un paiement différé jusqu\'à six mois après le décès du conjoint survivant en cas de nue-propriété, ou choisir de payer en nature en remettant à l\'État une œuvre d\'art ou un bien immobilier de valeur équivalente.',
  },
  {
    q: 'La déclaration est-elle confidentielle ?',
    a: 'Oui. Le contenu de la déclaration de succession est couvert par le secret professionnel du notaire et n\'est transmis qu\'à l\'administration fiscale. HÉRITA, de son côté, supprime votre document après analyse et ne le partage jamais avec un tiers.',
  },
];

const BREADCRUMB = [
  { label: 'Accueil', to: '/' },
  { label: 'Guides', to: '/comprendre-document-notarie' },
  { label: 'Déclaration de succession' },
];

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

const SEO_TITLE = 'Déclaration de succession : guide complet | HÉRITA';
const SEO_DESCRIPTION =
  "Comprenez facilement une déclaration de succession. Découvrez son rôle, son contenu, les informations importantes qu'elle contient et analysez votre propre document grâce à HÉRITA.";

const DeclarationSuccession = () => {
  return (
    <>
      <SEO
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        path="/declaration-succession"
        ogTitle="Déclaration de succession — Guides HÉRITA"
        ogDescription={SEO_DESCRIPTION}
      />
      <LibraryStructuredData
        path="/declaration-succession"
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        breadcrumb={BREADCRUMB}
        faq={FAQ_ITEMS}
        howTo={HOWTO_DECL}
        datePublished="2026-02-18"
        dateModified="2026-02-18"
      />

      <LibraryPageLayout
        breadcrumb={BREADCRUMB}
        testId="page-declaration-succession"
      >
        {/* ───────────── HERO ───────────── */}
        <LibraryHero
          eyebrow="Guides HÉRITA"
          title="Comprendre une déclaration de succession"
          subtitle="La déclaration de succession est l'un des documents les plus importants après un décès. HÉRITA vous aide à comprendre ce qu'elle contient, ce qu'elle implique pour vous et les informations essentielles à vérifier."
          trustItems={[
            'Analyse gratuite au départ',
            'Aucun compte requis',
            'Documents supprimés après analyse',
          ]}
        />

        {/* META — reading time, last update, table of contents */}
        <GuideMeta
          readingTime="9 min"
          lastUpdated="18 février 2026"
          sections={[
            { id: 'definition', label: 'Définition' },
            { id: 'utilite', label: 'Utilité' },
            { id: 'contenu', label: 'Contenu' },
            { id: 'vigilance', label: 'Vigilance' },
            { id: 'analyse', label: 'Analyse HÉRITA' },
            { id: 'exemple', label: 'Exemple' },
            { id: 'faq', label: 'FAQ' },
          ]}
        />

        {/* ───────── DÉFINITION ───────── */}
        <section
          id="definition"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          aria-labelledby="def-heading"
        >
          <div className="max-w-3xl mx-auto">
            <SectionHeader
              eyebrow="Définition"
              title={"Qu'est-ce qu'une déclaration de succession\u00A0?"}
            />
            <h2 id="def-heading" className="sr-only">
              Définition d'une déclaration de succession
            </h2>

            <div className="font-['Work_Sans'] text-[15.5px] sm:text-[16px] text-[#2C363F] leading-[1.85] space-y-6">
              <p>
                La déclaration de succession est un document à la fois{' '}
                <strong className="font-semibold text-[#1A2530]">administratif et fiscal</strong>.
                Elle dresse l'inventaire complet de ce qu'a laissé une personne
                au moment de son décès et permet à l'administration fiscale de
                calculer les droits que les héritiers devront verser à l'État.
              </p>

              <p>
                Concrètement, c'est un formulaire — le formulaire n°&nbsp;2705 et
                ses annexes — auquel sont jointes des pièces justificatives.
                Lorsqu'un notaire est en charge de la succession, c'est lui qui
                la prépare. Il y consigne, l'une après l'autre, toutes les
                informations utiles&nbsp;: l'état civil du défunt, le nom de
                chaque héritier, la liste des biens, la liste des dettes, les
                donations consenties autrefois.
              </p>

              <p>
                <strong className="font-semibold text-[#1A2530]">Pourquoi est-elle si importante&nbsp;?</strong>{' '}
                Parce qu'elle est l'acte qui détermine <em>combien</em> chacun
                des héritiers reçoit, et <em>combien</em> il doit payer à
                l'État. C'est la photographie officielle du patrimoine du
                défunt à la date de son décès. Tant qu'elle n'est pas déposée,
                la succession ne peut être réglée — ni la maison vendue, ni les
                comptes débloqués, ni les contrats d'assurance-vie versés.
              </p>

              <p>
                <strong className="font-semibold text-[#1A2530]">Pas un acte de propriété, mais une étape essentielle.</strong>{' '}
                Beaucoup confondent la déclaration de succession avec
                l'attestation immobilière ou l'acte de notoriété. Ce sont trois
                documents différents qui interviennent dans la même
                succession&nbsp;: la déclaration est <em>fiscale</em> et listée
                à Bercy&nbsp;; l'<Link to="/acte-notoriete" className="text-[#1A2530] font-medium underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530]">acte de notoriété</Link> établit la <em>qualité
                d'héritier</em>&nbsp;; l'attestation immobilière transfère
                officiellement <em>la propriété</em> d'un bien immobilier.
              </p>

              <p>
                Dans la pratique, la déclaration de succession est souvent le
                document <strong className="font-semibold text-[#1A2530]">le plus volumineux et le plus difficile à lire</strong> que
                vous recevrez du notaire. Elle peut contenir trente, cinquante
                ou cent pages, avec des tableaux, des annexes, des renvois au
                Code général des impôts. Elle utilise un vocabulaire
                particulier&nbsp;: « actif brut », « passif déductible »,
                « rapport à la succession », « usufruit légal du conjoint
                survivant »…
              </p>

              <p>
                C'est précisément là qu'HÉRITA intervient. L'objectif n'est
                pas de remplacer votre notaire — il reste votre interlocuteur
                officiel. L'objectif est de vous permettre, en quelques
                minutes, de comprendre quoi vous concerne, quels chiffres sont
                en jeu, et quelles questions poser ensuite.
              </p>
            </div>
          </div>
        </section>

        {/* ───────── À QUOI SERT ───────── */}
        <section
          id="utilite"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 scroll-mt-24"
          aria-labelledby="use-heading"
        >
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              eyebrow="Utilité"
              title={"À quoi sert ce document\u00A0?"}
            />
            <h2 id="use-heading" className="sr-only">À quoi sert la déclaration de succession</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  title: 'Pourquoi elle est établie',
                  description: 'Pour permettre à l\'administration fiscale de calculer les droits de succession et de vérifier qu\'aucun bien n\'a été oublié dans le patrimoine du défunt.',
                },
                {
                  title: 'Qui la prépare',
                  description: 'Le notaire dans la grande majorité des cas, surtout en présence d\'un bien immobilier, d\'un testament ou d\'un patrimoine supérieur à 5 000 €.',
                },
                {
                  title: 'Quand elle intervient',
                  description: 'Dans les six mois qui suivent le décès en France métropolitaine. Douze mois si le décès a eu lieu à l\'étranger ou dans certains DOM.',
                },
                {
                  title: 'Pourquoi elle est importante',
                  description: 'Sans déclaration déposée, la succession reste bloquée : ni vente immobilière, ni déblocage de comptes, ni versement d\'assurance-vie ne sont possibles.',
                },
              ].map(({ title, description }) => (
                <article
                  key={title}
                  className="bg-white border border-[#E2DBCF] rounded-2xl p-7"
                >
                  <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[24px] text-[#1A2530] mb-3 leading-tight">
                    {title}
                  </h3>
                  <p className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#2C363F] leading-relaxed">
                    {description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ───────── CONTENU ───────── */}
        <section
          id="contenu"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          aria-labelledby="content-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Contenu"
              title={"Que contient une déclaration de succession\u00A0?"}
              intro="Huit blocs d'information principaux composent une déclaration. Chacun a son rôle, et c'est leur articulation qui aboutit aux droits à payer."
            />
            <h2 id="content-heading" className="sr-only">Contenu d'une déclaration de succession</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {CONTENT_CARDS.map(({ Icon, title, description }) => (
                <article
                  key={title}
                  className="bg-[#F9F7F3] border border-[#E2DBCF] rounded-2xl p-6 hover:shadow-md hover:border-[#D3CCBE] transition-all"
                >
                  <div className="w-11 h-11 rounded-full bg-[#E3D5CA] flex items-center justify-center mb-5">
                    <Icon className="h-5 w-5 text-[#1A2530]" aria-hidden="true" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] font-medium text-[20px] sm:text-[22px] text-[#1A2530] mb-2 leading-tight">
                    {title}
                  </h3>
                  <p className="font-['Work_Sans'] text-[13.5px] text-[#2C363F] leading-relaxed">
                    {description}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ───────── ERREURS ───────── */}
        <section
          id="vigilance"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 scroll-mt-24"
          aria-labelledby="errors-heading"
        >
          <div className="max-w-3xl mx-auto">
            <SectionHeader
              eyebrow="Vigilance"
              title="Les erreurs les plus fréquentes"
              intro="Une déclaration de succession est dense et précise. Voici les oublis qui reviennent le plus souvent — et qui peuvent coûter cher."
            />
            <h2 id="errors-heading" className="sr-only">Erreurs fréquentes dans une déclaration de succession</h2>

            <ul className="space-y-3 list-none p-0">
              {COMMON_ERRORS.map((err, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 bg-white border border-[#E2DBCF] rounded-xl p-5"
                >
                  <span className="mt-0.5 w-6 h-6 rounded-full bg-[#E3D5CA] flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="h-3.5 w-3.5 text-[#1A2530]" aria-hidden="true" />
                  </span>
                  <span className="font-['Work_Sans'] text-[14.5px] text-[#1A2530] leading-snug">
                    {err}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ───────── AUTO CHECKS ───────── */}
        <section
          id="analyse"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          aria-labelledby="auto-heading"
        >
          <div className="max-w-5xl mx-auto">
            <SectionHeader
              eyebrow="Sans rien demander"
              title="Ce que HÉRITA identifie automatiquement"
              intro="Dès l'import de votre déclaration, HÉRITA repère l'essentiel et le réécrit en français clair."
            />
            <h2 id="auto-heading" className="sr-only">Ce que HÉRITA analyse</h2>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 list-none p-0">
              {AUTO_CHECKS.map((label) => (
                <FeatureCheckItem key={label} label={label} />
              ))}
            </ul>
          </div>
        </section>

        {/* ───────── EXEMPLE ───────── */}
        <section
          id="exemple"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 scroll-mt-24"
          aria-labelledby="example-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Avant · Après"
              title="Un exemple simplifié"
              intro="À gauche, un extrait fictif tel qu'on en trouve dans une déclaration de succession. À droite, ce qu'HÉRITA en aurait expliqué."
            />
            <h2 id="example-heading" className="sr-only">Exemple avant et après</h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
              {/* Left — fictional extract */}
              <article className="bg-[#F9F7F3] border border-[#E2DBCF] rounded-2xl p-7 sm:p-9 font-['Cormorant_Garamond'] text-[#1A2530]">
                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#2C363F] opacity-80 mb-4">
                  Extrait du document (fictif)
                </p>
                <p className="text-[16px] sm:text-[17px] leading-[1.85] italic">
                  « Déclarent que la succession de Monsieur Pierre DURAND,
                  décédé à Paris le 14 mars 2024, est composée de l'actif et du
                  passif suivants&nbsp;:<br /><br />
                  <span className="not-italic">— Maison sise [Adresse], lots 1 et 2 : <strong>520&nbsp;000&nbsp;€</strong></span><br />
                  <span className="not-italic">— Compte courant Crédit Agricole n°XXX : <strong>18&nbsp;472&nbsp;€</strong></span><br />
                  <span className="not-italic">— Assurance-vie Generali n°YYY : <strong>62&nbsp;000&nbsp;€</strong></span><br />
                  <span className="not-italic">— Passif déductible (crédit Habitat) : <strong>(38&nbsp;500&nbsp;€)</strong></span><br /><br />
                  À recueillir par moitié indivise par&nbsp;: Madame Léa DURAND
                  (épouse survivante, en usufruit total) et Mademoiselle Camille
                  DURAND (fille unique, en nue-propriété). »
                </p>
              </article>

              {/* Right — HÉRITA */}
              <article className="bg-white border-t-2 border-t-[#1A2530] border-x border-b border-[#E2DBCF] rounded-2xl p-7 sm:p-9 shadow-md">
                <p className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#1A2530] mb-4">
                  Lecture HÉRITA
                </p>
                <h3 className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] mb-5 leading-tight">
                  Ce que cela signifie pour Camille
                </h3>

                <ul className="space-y-3.5 list-none p-0">
                  {[
                    { strong: 'Patrimoine total déclaré', rest: ' : 600 472 € d\'actif, 38 500 € de dettes, soit un actif net de 561 972 €.' },
                    { strong: 'Vous héritez en nue-propriété', rest: ' de l\'ensemble : maison, compte et assurance-vie.' },
                    { strong: 'Votre mère, conjointe survivante', rest: ', conserve l\'usufruit total : elle peut habiter la maison et percevoir les revenus.' },
                    { strong: 'Aucun droit à payer immédiatement', rest: ' : votre abattement personnel de 100 000 € couvre votre part taxable.' },
                    { strong: 'À sa disparition', rest: ', vous deviendrez pleine propriétaire, sans nouveau droit à acquitter.' },
                  ].map((line, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#1A2530] flex-shrink-0" />
                      <span className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#1A2530] leading-snug">
                        <strong className="font-semibold">{line.strong}</strong>{line.rest}
                      </span>
                    </li>
                  ))}
                </ul>

                <p className="mt-6 pt-5 border-t border-[#E2DBCF] font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-80 flex items-center gap-2">
                  <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
                  Lecture réalisée en moins de 30 secondes
                </p>
              </article>
            </div>

            <p className="mt-8 font-['Work_Sans'] text-[12.5px] text-[#2C363F] italic text-center opacity-80">
              Exemple illustratif. Les noms, montants et adresses sont inventés
              et ne correspondent à aucun dossier réel.
            </p>
          </div>
        </section>

        {/* ───────── FAQ ───────── */}
        <section
          id="faq"
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-y border-[#E2DBCF] scroll-mt-24"
          aria-labelledby="faq-heading"
        >
          <div className="max-w-4xl mx-auto">
            <SectionHeader
              eyebrow="Questions fréquentes"
              title="Tout ce que vous voulez savoir"
              intro="Dix questions concrètes pour comprendre la déclaration de succession sans détour."
            />
            <h2 id="faq-heading" className="sr-only">FAQ déclaration de succession</h2>

            <LibraryFAQ items={FAQ_ITEMS} defaultOpen={0} />
          </div>
        </section>

        {/* ───────── FINAL CTA ───────── */}
        <LibraryFinalCta
          title={"Vous avez reçu une déclaration de succession\u00A0?"}
          subtitle="Déposez simplement votre document. HÉRITA identifie automatiquement les personnes, les biens, les montants et les démarches importantes afin de vous fournir une explication claire et personnalisée."
        />

        {/* ───────── OFFICIAL SOURCES ───────── */}
        <OfficialSources sources={SOURCES_DECL} />

        {/* ───────── DÉCOUVRIR AUSSI ───────── */}
        <section
          className="px-5 sm:px-6 md:px-12 lg:px-24 py-16 sm:py-20 bg-white border-t border-[#E2DBCF]"
          aria-labelledby="more-heading"
        >
          <div className="max-w-6xl mx-auto">
            <SectionHeader
              eyebrow="Aller plus loin"
              title="Guides associés"
              intro="Les autres guides HÉRITA pour comprendre les actes notariés qui peuvent croiser votre succession."
            />
            <h2 id="more-heading" className="sr-only">Découvrir aussi</h2>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {relatedGuides('/declaration-succession').map(({ label, to }) => (
                <Link
                  key={to}
                  to={to}
                  className="group block bg-[#F9F7F3] border border-[#E2DBCF] rounded-xl p-5 hover:border-[#1A2530] hover:bg-white transition-all"
                  data-testid={`more-link-${to}`}
                >
                  <p className="font-['Work_Sans'] text-[10px] uppercase tracking-[0.22em] text-[#2C363F] opacity-70 mb-2">
                    Comprendre
                  </p>
                  <p className="font-['Cormorant_Garamond'] text-[19px] sm:text-[21px] text-[#1A2530] leading-tight mb-3 font-medium">
                    {label}
                  </p>
                  <span className="inline-flex items-center font-['Work_Sans'] text-[12.5px] font-medium text-[#1A2530] group-hover:underline underline-offset-[5px] decoration-[#1A2530]">
                    Lire le guide
                    <ArrowRight className="ml-1 h-3 w-3 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* end */}
      </LibraryPageLayout>
    </>
  );
};

export default DeclarationSuccession;
